import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  FiUser,
  FiClipboard,
  FiBarChart2,
  FiPlus,
  FiChevronDown,
  FiCheck,
  FiPrinter,
  FiInbox,
  FiEdit2,
} from "react-icons/fi";

import Sidebar from "../../Component/Sidebar/Sidebar";
import Header from "../../Component/Header/Header";

import EditSettlementModal from "./EditSettlementModal";
import {
  fetchRepresentatives,
  fetchSummary,
  fetchSettlements,
  createSettlement,
  updateSettlement,
} from "./settlementApi";
import { printSettlement } from "./settlementPrint";
import { money, formatDate } from "./settlementFormatters";

const RepresentativeSettlement = () => {
  // =========================================================
  // States
  // =========================================================

  const [representatives, setRepresentatives] = useState([]);
  const [loadingRepresentatives, setLoadingRepresentatives] = useState(false);

  const [representativeId, setRepresentativeId] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);

  const [representativeSearch, setRepresentativeSearch] = useState("");
  const [showRepresentativeDropdown, setShowRepresentativeDropdown] = useState(false);

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [settlements, setSettlements] = useState([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);

  const [activeTab, setActiveTab] = useState("current");
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);

  // Edit (PUT)
  const [editingSettlement, setEditingSettlement] = useState(null);
  const [updatingSettlement, setUpdatingSettlement] = useState(false);

  const [settlementForm, setSettlementForm] = useState({
    shippingPerOrder: "",
    cashAmount: "",
    instaPayAmount: "",
    vodafoneCashAmount: "",
    paidAmount: "",
    notes: "",
  });

  // =========================================================
  // Get Representatives
  // =========================================================

  const getRepresentatives = async () => {
    try {
      setLoadingRepresentatives(true);

      const data = await fetchRepresentatives();
      setRepresentatives(data);
    } catch (error) {
      console.error("GET REPRESENTATIVES ERROR:", error);
      Swal.fire({ icon: "error", title: "خطأ", text: "حدث خطأ أثناء جلب المندوبين", confirmButtonText: "حسناً" });
    } finally {
      setLoadingRepresentatives(false);
    }
  };

  // =========================================================
  // Initial Load
  // =========================================================

  useEffect(() => {
    getRepresentatives();
  }, []);

  // =========================================================
  // Selected Representative
  // =========================================================

  const selectedRepresentative = useMemo(() => {
    return representatives.find((rep) => Number(rep.id) === Number(representativeId));
  }, [representatives, representativeId]);

  // =========================================================
  // Filter Representatives
  // =========================================================

  const filteredRepresentatives = useMemo(() => {
    const search = representativeSearch.trim().toLowerCase();
    if (!search) return representatives;

    return representatives.filter((rep) => {
      const name = String(rep?.fullName || "").toLowerCase();
      const phone = String(rep?.phoneNumber || "").toLowerCase();
      return name.includes(search) || phone.includes(search);
    });
  }, [representatives, representativeSearch]);

  // =========================================================
  // Select Representative
  // =========================================================

  const handleSelectRepresentative = (rep) => {
    setRepresentativeId(String(rep.id));
    setRepresentativeSearch("");
    setShowRepresentativeDropdown(false);

    // نخفي النتائج القديمة
    setSummary(null);
    setSettlements([]);
    setShowSettlementForm(false);
    setActiveTab("current");
  };

  // =========================================================
  // Get Summary
  // =========================================================

  const getSummary = async () => {
    if (!representativeId) {
      Swal.fire({ icon: "warning", title: "اختر المندوب", text: "من فضلك اختر المندوب أولاً", confirmButtonText: "حسناً" });
      return;
    }

    if (!selectedDate) {
      Swal.fire({ icon: "warning", title: "اختر التاريخ", text: "من فضلك اختر التاريخ", confirmButtonText: "حسناً" });
      return;
    }

    try {
      setLoadingSummary(true);
      setSummary(null);
      setSettlements([]);
      setShowSettlementForm(false);

      const data = await fetchSummary(representativeId, selectedDate);
      setSummary(data);

      // هات سجل التسويات
      await getSettlements(representativeId, selectedDate);
    } catch (error) {
      console.error("GET SUMMARY ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء جلب بيانات التصفية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setLoadingSummary(false);
    }
  };

  // =========================================================
  // Get Settlement Records
  // =========================================================

  const getSettlements = async (repId, date) => {
    if (!repId || !date) return;

    try {
      setLoadingSettlements(true);

      const data = await fetchSettlements(repId, date);
      setSettlements(data);
    } catch (error) {
      console.error("GET SETTLEMENTS ERROR:", error);
      setSettlements([]);
    } finally {
      setLoadingSettlements(false);
    }
  };

  // =========================================================
  // Date Change
  // =========================================================

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSummary(null);
    setSettlements([]);
    setShowSettlementForm(false);
    setActiveTab("current");
  };

  // =========================================================
  // Settlement Form Change
  // =========================================================

  const handleSettlementChange = (e) => {
    const { name, value } = e.target;
    setSettlementForm((prev) => ({ ...prev, [name]: value }));
  };

  // =========================================================
  // Create Settlement
  // =========================================================

  const handleCreateSettlement = async (e) => {
    e.preventDefault();

    if (!selectedRepresentative) {
      Swal.fire({ icon: "warning", title: "اختر المندوب", text: "من فضلك اختر المندوب", confirmButtonText: "حسناً" });
      return;
    }

    if (!summary) {
      Swal.fire({ icon: "warning", title: "لا توجد بيانات", text: "اعرض بيانات المندوب أولاً", confirmButtonText: "حسناً" });
      return;
    }

    try {
      setSavingSettlement(true);

      /*
       * مهم:
       * مش بنبعت shippingAmount
       * ومش بنبعت paidAmount كـ total منفصل.
       *
       * الـ API هو اللي بيحسب القيم التلقائية.
       */

      const payload = {
        representativeId: Number(selectedRepresentative.id),
        settlementDate: `${selectedDate}T00:00:00`,
        shippingPerOrder: Number(settlementForm.shippingPerOrder) || 0,
        cashAmount: Number(settlementForm.cashAmount) || 0,
        instaPayAmount: Number(settlementForm.instaPayAmount) || 0,
        vodafoneCashAmount: Number(settlementForm.vodafoneCashAmount) || 0,
        notes: settlementForm.notes || "",
      };

      await createSettlement(payload);

      Swal.fire({ icon: "success", title: "تمت التسوية", text: "تم حفظ التسوية بنجاح", confirmButtonText: "حسناً" });

      setShowSettlementForm(false);
      setSettlementForm({
        shippingPerOrder: "",
        cashAmount: "",
        instaPayAmount: "",
        vodafoneCashAmount: "",
        paidAmount: "",
        notes: "",
      });

      // تحديث الـ summary
      await getSummary();

      // تحديث سجل التسويات
      await getSettlements(selectedRepresentative.id, selectedDate);
    } catch (error) {
      console.error("CREATE SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء حفظ التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setSavingSettlement(false);
    }
  };

  // =========================================================
  // Update Settlement (PUT)
  // =========================================================

  const handleUpdateSettlement = async (form) => {
    if (!editingSettlement) return;

    try {
      setUpdatingSettlement(true);

      const cashAmount = Number(form.cashAmount) || 0;
      const instaPayAmount = Number(form.instaPayAmount) || 0;
      const vodafoneCashAmount = Number(form.vodafoneCashAmount) || 0;

      const payload = {
        cashAmount,
        instaPayAmount,
        vodafoneCashAmount,
        paidAmount: cashAmount + instaPayAmount + vodafoneCashAmount,
        shippingPerOrder: Number(form.shippingPerOrder) || 0,
        shippingAmount: Number(editingSettlement.shippingAmount) || 0,
        notes: form.notes || "",
      };

      await updateSettlement(editingSettlement.id, payload);

      Swal.fire({ icon: "success", title: "تم التعديل", text: "تم تعديل التسوية بنجاح", confirmButtonText: "حسناً" });

      setEditingSettlement(null);

      // تحديث الـ summary وسجل التسويات
      await getSummary();
    } catch (error) {
      console.error("UPDATE SETTLEMENT ERROR:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error?.response?.data?.message || "حدث خطأ أثناء تعديل التسوية",
        confirmButtonText: "حسناً",
      });
    } finally {
      setUpdatingSettlement(false);
    }
  };

  // =========================================================
  // Print Settlement
  // =========================================================

  const handlePrintSettlement = (item) => {
    const opened = printSettlement(item);

    if (!opened) {
      Swal.fire({
        icon: "warning",
        title: "تعذر فتح نافذة الطباعة",
        text: "من فضلك اسمح بالنوافذ المنبثقة (popups) لهذا الموقع وحاول مرة أخرى",
        confirmButtonText: "حسناً",
      });
    }
  };

  // =========================================================
  // Helpers
  // =========================================================

  /*
   * المتبقي:
   *
   * موجب  => المندوب عليه فلوس
   * صفر   => تم التقفيل
   * سالب  => الشركة عليها فلوس للمندوب
   */

  const renderRemaining = (remaining) => {
    const value = Number(remaining || 0);

    if (value < 0) {
      return (
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
          <p className="text-sm font-bold text-purple-700">مستحقات المندوب</p>
          <p className="mt-1 text-2xl font-black text-purple-700">{money(Math.abs(value))} ج.م</p>
          <p className="mt-1 text-xs text-purple-600">المندوب له هذا المبلغ</p>
        </div>
      );
    }

    if (value === 0) {
      return (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-bold text-green-700">حالة الحساب</p>
          <p className="mt-1 text-2xl font-black text-green-700">تم التقفيل</p>
          <p className="mt-1 text-xs text-green-600">لا يوجد مبلغ متبقي</p>
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p className="text-sm font-bold text-red-700">المتبقي على المندوب</p>
        <p className="mt-1 text-2xl font-black text-red-700">{money(value)} ج.م</p>
        <p className="mt-1 text-xs text-red-600">المندوب عليه هذا المبلغ</p>
      </div>
    );
  };

  const getStatusClass = (status) => {
    if (!status) return "bg-slate-100 text-slate-700";
    if (status.includes("مرتجع") || status.includes("راجع")) return "bg-red-100 text-red-700";
    if (status.includes("تم") || status.includes("تسليم")) return "bg-green-100 text-green-700";
    if (status.includes("تحت") || status.includes("تجهيز")) return "bg-yellow-100 text-yellow-700";
    return "bg-blue-100 text-blue-700";
  };

  // =========================================================
  // Render
  // =========================================================

  return (
    <div className="apDiv ChatApp min-h-screen bg-slate-50">
      <Sidebar />

      <div className="body_container container" dir="rtl">
        <div className="Header">
          <Header />
        </div>

        <main className="px-4 py-6 md:px-6 lg:px-8">
          {/* PAGE HEADER */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800">تسوية المندوبين</h1>
            <p className="mt-1 text-sm text-slate-500">اختر المندوب والتاريخ لمراجعة التصفية وإجراء التسوية</p>
          </div>

          {/* FILTER BOX */}
          <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5">
              <h2 className="text-lg font-bold text-slate-800">بيانات التصفية</h2>
              <p className="mt-1 text-sm text-slate-500">اختر المندوب والتاريخ ثم اضغط عرض التصفية</p>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {/* REPRESENTATIVE DROPDOWN */}
              <div className="relative">
                <label className="mb-2 block text-sm font-bold text-slate-700">المندوب</label>

                <button
                  type="button"
                  onClick={() => setShowRepresentativeDropdown((prev) => !prev)}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-300 bg-white px-4 py-3 text-right outline-none transition hover:border-blue-400 focus:border-blue-500"
                >
                  {selectedRepresentative ? (
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 font-bold text-blue-700">
                        {selectedRepresentative.fullName?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800">{selectedRepresentative.fullName}</p>
                        <p className="text-xs text-slate-500">{selectedRepresentative.phoneNumber}</p>
                      </div>
                    </div>
                  ) : (
                    <span className="text-slate-400">اختر المندوب</span>
                  )}
                  <FiChevronDown
                    className={`text-slate-400 transition-transform ${showRepresentativeDropdown ? "rotate-180" : ""}`}
                  />
                </button>

                {showRepresentativeDropdown && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                    <div className="border-b border-slate-200 p-3">
                      <input
                        type="text"
                        autoFocus
                        value={representativeSearch}
                        onChange={(e) => setRepresentativeSearch(e.target.value)}
                        placeholder="ابحث باسم المندوب أو رقم الهاتف..."
                        className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:bg-white"
                      />
                    </div>

                    <div className="max-h-72 overflow-y-auto">
                      {loadingRepresentatives ? (
                        <div className="p-5 text-center text-sm text-slate-500">جاري تحميل المندوبين...</div>
                      ) : filteredRepresentatives.length === 0 ? (
                        <div className="p-5 text-center text-sm text-slate-500">لا يوجد مندوب بهذا الاسم</div>
                      ) : (
                        filteredRepresentatives.map((rep) => {
                          const selected = Number(representativeId) === Number(rep.id);
                          return (
                            <button
                              key={rep.id}
                              type="button"
                              onClick={() => handleSelectRepresentative(rep)}
                              className={`flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-right transition last:border-b-0 ${
                                selected ? "bg-blue-50" : "hover:bg-slate-50"
                              }`}
                            >
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 font-bold text-slate-600">
                                {rep.fullName?.charAt(0)}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-bold text-slate-800">{rep.fullName}</p>
                                <p className="text-xs text-slate-500">
                                  {rep.phoneNumber || "بدون رقم"} - {rep?.governorate?.name}
                                </p>
                              </div>
                              {selected && <FiCheck className="text-blue-600" />}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* DATE */}
              <div>
                <label className="mb-2 block text-sm font-bold text-slate-700">تاريخ التصفية</label>

                <input
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                />

                <button
                  type="button"
                  onClick={getSummary}
                  disabled={loadingSummary || !representativeId}
                  className="mt-4 w-full rounded-xl bg-blue-600 px-5 py-3 font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loadingSummary ? "جاري التحميل..." : "عرض التصفية"}
                </button>
              </div>
            </div>
          </section>

          {/* TABS */}
          {summary && (
            <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("current")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold transition ${
                    activeTab === "current" ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <FiClipboard />
                  التصفية الحالية
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("history")}
                  className={`flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold transition ${
                    activeTab === "history" ? "bg-blue-600 text-white shadow" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <FiBarChart2 />
                  سجل التسويات
                  {settlements.length > 0 && (
                    <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{settlements.length}</span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* NO DATA */}
          {!summary && !loadingSummary && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-3xl text-blue-600">
                <FiUser />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-800">اختر المندوب والتاريخ</h3>
              <p className="mt-2 text-sm text-slate-500">ثم اضغط "عرض التصفية"</p>
            </div>
          )}

          {/* LOADING */}
          {loadingSummary && (
            <div className="rounded-2xl bg-white py-16 text-center shadow-sm">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
              <p className="mt-4 text-sm text-slate-500">جاري تحميل بيانات التصفية...</p>
            </div>
          )}

          {/* CURRENT SETTLEMENT TAB */}
          {summary && !loadingSummary && activeTab === "current" && (
            <>
              {/* Representative Header */}
              <section className="mb-6 rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-xl font-bold text-white">
                      {summary.representativeName?.charAt(0) || "م"}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{summary.representativeName}</h2>
                      <p className="mt-1 text-sm text-slate-500">تاريخ التصفية: {formatDate(summary.settlementDate)}</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowSettlementForm((prev) => !prev)}
                    className="flex items-center justify-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-bold text-white transition hover:bg-green-700"
                  >
                    {!showSettlementForm && <FiPlus />}
                    {showSettlementForm ? "إغلاق التسوية" : "عمل تسوية"}
                  </button>
                </div>
              </section>

              {/* Stats */}
              <section className="mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">إجمالي الفواتير</p>
                    <p className="mt-2 text-3xl font-black text-slate-800">{summary.totalHandedInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">إجمالي المبلغ</p>
                    <p className="font-bold text-slate-800">{money(summary.totalHandedAmount)} ج.م</p>
                  </div>

                  <div className="rounded-2xl border border-green-100 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">تم التسليم</p>
                    <p className="mt-2 text-3xl font-black text-green-600">{summary.deliveredInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">المبلغ</p>
                    <p className="font-bold text-green-700">{money(summary.deliveredAmount)} ج.م</p>
                  </div>

                  <div className="rounded-2xl border border-red-100 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">المرتجعات</p>
                    <p className="mt-2 text-3xl font-black text-red-600">{summary.returnedInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">المبلغ</p>
                    <p className="font-bold text-red-700">{money(summary.returnedAmount)} ج.م</p>
                  </div>

                  <div className="rounded-2xl border border-yellow-100 bg-white p-5 shadow-sm">
                    <p className="text-sm text-slate-500">المعلّق</p>
                    <p className="mt-2 text-3xl font-black text-yellow-600">{summary.pendingInvoicesCount}</p>
                    <p className="mt-2 text-sm text-slate-500">المبلغ</p>
                    <p className="font-bold text-yellow-700">{money(summary.pendingAmount)} ج.م</p>
                  </div>
                </div>
              </section>

              {/* Financial */}
              <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="mb-5 text-lg font-bold text-slate-800">التفاصيل المالية</h2>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <p className="text-sm text-slate-500">إجمالي المبلغ</p>
                    <p className="mt-2 text-xl font-black">{money(summary.totalHandedAmount)} ج.م</p>
                  </div>

                  <div className="rounded-xl bg-orange-50 p-4">
                    <p className="text-sm text-orange-700">إجمالي الشحن</p>
                    <p className="mt-2 text-xl font-black text-orange-700">
                      {money(summary.shippingAmount ?? summary.totalShippingAmount)} ج.م
                    </p>
                  </div>

                  <div className="rounded-xl bg-blue-50 p-4">
                    <p className="text-sm text-blue-700">صافي المطلوب</p>
                    <p className="mt-2 text-xl font-black text-blue-700">{money(summary.netRequiredAmount)} ج.م</p>
                  </div>

                  {renderRemaining(summary.remainingAmount)}
                </div>
              </section>

              {/* Settlement Form */}
              {showSettlementForm && (
                <section className="mb-6 rounded-2xl border border-green-200 bg-white shadow-sm">
                  <div className="border-b border-green-100 bg-green-50 p-5">
                    <h2 className="text-lg font-bold text-green-800">عمل تسوية</h2>
                    <p className="mt-1 text-sm text-green-700">أدخل مبالغ الدفع فقط، وسيتم حساب الإجماليات تلقائيًا</p>
                  </div>

                  <form onSubmit={handleCreateSettlement} className="p-5">
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">الشحن لكل أوردر</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="shippingPerOrder"
                          value={settlementForm.shippingPerOrder}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">كاش</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="cashAmount"
                          value={settlementForm.cashAmount}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">InstaPay</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="instaPayAmount"
                          value={settlementForm.instaPayAmount}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-sm font-bold text-slate-700">Vodafone Cash</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          name="vodafoneCashAmount"
                          value={settlementForm.vodafoneCashAmount}
                          onChange={handleSettlementChange}
                          placeholder="0"
                          className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-bold text-slate-700">ملاحظات</label>
                        <textarea
                          name="notes"
                          rows="3"
                          value={settlementForm.notes}
                          onChange={handleSettlementChange}
                          placeholder="اكتب ملاحظات التسوية..."
                          className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                      </div>
                    </div>

                    <div className="mt-5 rounded-xl bg-blue-50 p-4">
                      <p className="text-sm font-bold text-blue-800">صافي المطلوب الحالي</p>
                      <p className="mt-1 text-2xl font-black text-blue-700">{money(summary.netRequiredAmount)} ج.م</p>
                      <p className="mt-2 text-xs text-blue-600">إجمالي الشحن وإجمالي المدفوع يتم حسابهما تلقائيًا بواسطة النظام.</p>
                    </div>

                    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={() => setShowSettlementForm(false)}
                        className="rounded-xl border border-slate-300 bg-white px-6 py-3 font-bold text-slate-700 hover:bg-slate-50"
                      >
                        إلغاء
                      </button>

                      <button
                        type="submit"
                        disabled={savingSettlement}
                        className="rounded-xl bg-green-600 px-8 py-3 font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {savingSettlement ? "جاري حفظ التسوية..." : "حفظ التسوية"}
                      </button>
                    </div>
                  </form>
                </section>
              )}

              {/* Orders */}
              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-200 p-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-slate-800">أوردرات المندوب</h2>
                    <p className="mt-1 text-sm text-slate-500">جميع الفواتير الخاصة بالمندوب في التاريخ المحدد</p>
                  </div>

                  <div className="rounded-xl bg-blue-50 px-4 py-2 text-sm font-bold text-blue-700">
                    {summary.allInvoices?.length || 0} أوردر
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1200px] text-right text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-4 font-bold text-slate-600">#</th>
                        <th className="px-4 py-4 font-bold text-slate-600">رقم الفاتورة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العميل</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الهاتف</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المحافظة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العنوان</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الحالة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المنتجات</th>
                        <th className="px-4 py-4 font-bold text-slate-600">العدد</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المبلغ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الشحن</th>
                        <th className="px-4 py-4 font-bold text-slate-600">التاريخ</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {summary.allInvoices?.length > 0 ? (
                        summary.allInvoices.map((invoice, index) => (
                          <tr key={invoice.id || index} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-bold text-slate-500">{index + 1}</td>
                            <td className="px-4 py-4 font-bold text-blue-600">#{invoice.invoiceCode}</td>
                            <td className="px-4 py-4 font-medium text-slate-800">{invoice.customer}</td>
                            <td className="px-4 py-4 text-slate-600">{invoice.phone}</td>
                            <td className="px-4 py-4 text-slate-600">{invoice.governorateName}</td>
                            <td className="max-w-[250px] px-4 py-4 text-xs leading-6 text-slate-500">{invoice.address}</td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </td>
                            <td className="max-w-[250px] px-4 py-4 text-xs leading-6 text-slate-600">{invoice.itemsDescription}</td>
                            <td className="px-4 py-4 text-center">
                              <span className="rounded-lg bg-slate-100 px-3 py-1 font-bold">{invoice.itemsCount}</span>
                            </td>
                            <td className="px-4 py-4 font-bold">{money(invoice.totalPrice)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-orange-600">{money(invoice.shipping)} ج.م</td>
                            <td className="px-4 py-4 text-xs text-slate-500">{formatDate(invoice.dailyDate)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="12" className="px-4 py-12 text-center text-slate-500">
                            لا توجد أوردرات
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {/* HISTORY TAB */}
          {summary && !loadingSummary && activeTab === "history" && (
            <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 p-5">
                <h2 className="text-lg font-bold text-slate-800">سجل التسويات</h2>
                <p className="mt-1 text-sm text-slate-500">جميع عمليات التسوية للمندوب في التاريخ المحدد</p>
              </div>

              {loadingSettlements ? (
                <div className="py-16 text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                  <p className="mt-3 text-sm text-slate-500">جاري تحميل سجل التسويات...</p>
                </div>
              ) : settlements.length === 0 ? (
                <div className="py-16 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl text-slate-500">
                    <FiInbox />
                  </div>
                  <h3 className="mt-4 font-bold text-slate-700">لا توجد تسويات</h3>
                  <p className="mt-1 text-sm text-slate-500">لا يوجد سجل تسوية لهذا المندوب في هذا التاريخ</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1600px] text-right text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-4 font-bold text-slate-600">#</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المندوب</th>
                        <th className="px-4 py-4 font-bold text-slate-600">التاريخ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الفواتير</th>
                        <th className="px-4 py-4 font-bold text-slate-600">إجمالي المبلغ</th>
                        <th className="px-4 py-4 font-bold text-slate-600">تم التسليم</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المرتجع</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المعلّق</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الشحن</th>
                        <th className="px-4 py-4 font-bold text-slate-600">صافي المطلوب</th>
                        <th className="px-4 py-4 font-bold text-slate-600">كاش</th>
                        <th className="px-4 py-4 font-bold text-slate-600">InstaPay</th>
                        <th className="px-4 py-4 font-bold text-slate-600">Vodafone Cash</th>
                        <th className="px-4 py-4 font-bold text-slate-600">المدفوع</th>
                        <th className="px-4 py-4 font-bold text-slate-600">النتيجة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">الحالة</th>
                        <th className="px-4 py-4 font-bold text-slate-600">ملاحظات</th>
                        <th className="px-4 py-4 font-bold text-slate-600 text-center">إجراءات</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {settlements.map((item, index) => {
                        const remaining = Number(item.remainingAmount || 0);

                        return (
                          <tr key={item.id || index} className="hover:bg-slate-50">
                            <td className="px-4 py-4 font-bold text-slate-500">{index + 1}</td>
                            <td className="px-4 py-4 font-bold text-slate-800">{item.representativeName}</td>
                            <td className="px-4 py-4">{formatDate(item.settlementDate)}</td>
                            <td className="px-4 py-4 font-bold">{item.totalHandedInvoicesCount}</td>
                            <td className="px-4 py-4 font-bold">{money(item.totalHandedAmount)} ج.م</td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-green-600">{item.deliveredInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.deliveredAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-red-600">{item.returnedInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.returnedAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4">
                              <span className="font-bold text-yellow-600">{item.pendingInvoicesCount}</span>
                              <div className="text-xs text-slate-500">{money(item.pendingAmount)} ج.م</div>
                            </td>

                            <td className="px-4 py-4 font-bold text-orange-600">{money(item.shippingAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-blue-600">{money(item.netRequiredAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-green-600">{money(item.cashAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-blue-600">{money(item.instaPayAmount)} ج.م</td>
                            <td className="px-4 py-4 font-bold text-red-600">{money(item.vodafoneCashAmount)} ج.م</td>

                            <td className="px-4 py-4">
                              <span className="rounded-lg bg-green-50 px-3 py-1 font-bold text-green-700">
                                {money(item.paidAmount)} ج.م
                              </span>
                            </td>

                            <td className="px-4 py-4">
                              {remaining < 0 ? (
                                <div>
                                  <span className="font-black text-purple-700">للمندوب</span>
                                  <div className="font-bold text-purple-700">{money(Math.abs(remaining))} ج.م</div>
                                </div>
                              ) : remaining === 0 ? (
                                <span className="font-bold text-green-600">تم التقفيل</span>
                              ) : (
                                <div>
                                  <span className="font-black text-red-600">على المندوب</span>
                                  <div className="font-bold text-red-600">{money(remaining)} ج.م</div>
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                                  item.status === "تم التقفيل"
                                    ? "bg-green-100 text-green-700"
                                    : item.status === "تقفيل جزئي"
                                    ? "bg-yellow-100 text-yellow-700"
                                    : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>

                            <td className="max-w-[220px] px-4 py-4 text-slate-500">{item.notes || "-"}</td>

                            <td className="px-4 py-4 text-center">
                              <div className="inline-flex items-center justify-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setEditingSettlement(item)}
                                  title="تعديل التسوية"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:border-green-400 hover:text-green-600"
                                >
                                  <FiEdit2 />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handlePrintSettlement(item)}
                                  title="طباعة PDF"
                                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 p-2 text-slate-600 transition hover:border-blue-400 hover:text-blue-600"
                                >
                                  <FiPrinter />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </main>
      </div>

      {/* EDIT SETTLEMENT MODAL */}
      {editingSettlement && (
        <EditSettlementModal
          key={editingSettlement.id}
          settlement={editingSettlement}
          saving={updatingSettlement}
          onClose={() => setEditingSettlement(null)}
          onSubmit={handleUpdateSettlement}
        />
      )}
    </div>
  );
};

export default RepresentativeSettlement;